import { createTheme, type Theme } from '@mui/material/styles';

/**
 * Monospaced font with tabular digits, for every amount, lot size and percentage, so
 * numbers line up in lists (mockups: `.num` class).
 */
export const NUMERIC_FONT = '"IBM Plex Mono", ui-monospace, monospace';

const TEXT_FONT = '"IBM Plex Sans", system-ui, -apple-system, "Segoe UI", sans-serif';

// Light palette: the colours of the FXMate mockups (docs/06-MiniApp/Maquettes, artifact
// "Maquettes FXMate"). The dark palette is not in the mockups: it keeps the same hues,
// lightened for contrast on dark surfaces. Proposal, to be validated.
const PALETTE = {
  light: {
    brand: '#0B6684',
    brandDark: '#084C62',
    page: '#F3F5F7',
    surface: '#FFFFFF',
    text: '#111821',
    textMuted: '#556070',
    border: '#DCE2E8',
    success: '#17784A',
    successBg: '#E5F4EC',
    danger: '#B42318',
    dangerBg: '#FBF3F2',
    warning: '#9A5B00',
    warningText: '#7A4800',
    warningBg: '#FFF1D6',
    info: '#0B6684',
    infoBg: '#E3F0F5',
    neutralBg: '#EEF1F4',
    // Channel initials avatars (mockups Main, Canaux, GestionCanaux), white text on each.
    avatars: ['#0B6684', '#5B4A8C', '#9A5B00', '#2F6B3E', '#6B7280'],
  },
  dark: {
    brand: '#5BB8D6',
    brandDark: '#3D9DBD',
    page: '#0F1419',
    surface: '#182028',
    text: '#E8EDF1',
    textMuted: '#9AA6B2',
    border: '#2A333D',
    success: '#4CC38A',
    successBg: '#16352A',
    danger: '#F0705F',
    dangerBg: '#3A1D1A',
    warning: '#E5A83B',
    warningText: '#F1C374',
    warningBg: '#3A2C12',
    info: '#5BB8D6',
    infoBg: '#14303B',
    neutralBg: '#222B34',
    avatars: ['#0B6684', '#5B4A8C', '#9A5B00', '#2F6B3E', '#6B7280'],
  },
} as const;

/** Colours of one scheme of the FXMate palette. */
type FxPalette = (typeof PALETTE)['light' | 'dark'];

declare module '@mui/material/styles' {
  interface Theme {
    /** FXMate colours that MUI's palette has no slot for (chip backgrounds, muted surfaces). */
    fx: FxPalette;
  }
  interface ThemeOptions {
    fx?: FxPalette;
  }
}

/**
 * Builds the MUI theme from the FXMate mockups, in the light or dark scheme of the
 * user's Telegram client (technical spec §4: follow the Telegram theme).
 */
export function createAppTheme(scheme: 'light' | 'dark'): Theme {
  const p = PALETTE[scheme];
  return createTheme({
    fx: p,
    palette: {
      mode: scheme,
      primary: {
        main: p.brand,
        dark: p.brandDark,
        contrastText: scheme === 'light' ? '#FFFFFF' : '#0F1419',
      },
      success: { main: p.success, light: p.successBg },
      error: { main: p.danger, light: p.dangerBg },
      warning: { main: p.warning, dark: p.warningText, light: p.warningBg },
      info: { main: p.info, light: p.infoBg },
      background: { default: p.page, paper: p.surface },
      text: { primary: p.text, secondary: p.textMuted },
      divider: p.border,
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: TEXT_FONT,
      fontSize: 15,
      h1: { fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em' },
      h2: { fontSize: 16, fontWeight: 600 },
      overline: { fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', lineHeight: 1.5 },
      body2: { fontSize: 13, lineHeight: 1.45 },
      button: { textTransform: 'none', fontWeight: 600, fontSize: 16 },
    },
    components: {
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: { outlined: { borderRadius: 16, borderColor: p.border } },
      },
      MuiCard: {
        defaultProps: { variant: 'outlined' },
        styleOverrides: { root: { borderRadius: 16 } },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 14, minHeight: 44 },
          sizeLarge: { minHeight: 52 },
          outlined: { borderWidth: 1.5, backgroundColor: p.surface },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 999, fontWeight: 600, fontSize: 12 },
          sizeSmall: { height: 24 },
        },
      },
      MuiLink: { defaultProps: { underline: 'none' } },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: { color: p.textMuted, '&.Mui-selected': { color: p.brand } },
          label: { fontSize: 12, '&.Mui-selected': { fontSize: 12, fontWeight: 600 } },
        },
      },
    },
  });
}
